// Shared storefront art — the one-motif (fern frond) system from the Taste-Skill
// direction (one-motif). The products are DRAWN as flat SVG in the brand colours
// because no product photography exists yet (OI-002); the leaf mask, the
// fiddlehead curl, and the two bottles all live here so the catalog, the PDP, and
// the demo page reference a single source.

const DEFS_ID = 'asm-motif-defs';

// The leaf clip-path (objectBoundingBox, scales with the box), the fiddlehead
// curl, and the drawn Liquid / Tablets bottles.
const DEFS = `<svg width="0" height="0" aria-hidden="true" style="position:absolute">
  <defs>
    <clipPath id="asm-leaf" clipPathUnits="objectBoundingBox">
      <path d="M0.5,0 C0.9,0.2 0.98,0.42 0.98,0.57 C0.98,0.79 0.76,0.97 0.5,1 C0.24,0.97 0.02,0.79 0.02,0.57 C0.02,0.42 0.1,0.2 0.5,0 Z"/>
    </clipPath>
    <symbol id="curl" viewBox="0 0 24 24"><path d="M20 12a8 8 0 1 1-8-8 6 6 0 1 1 6 6 4 4 0 1 1-4-4 2 2 0 0 1 2 2" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></symbol>
    <symbol id="asm-liquid" viewBox="0 0 120 200">
      <rect x="44" y="4" width="32" height="18" rx="5" fill="#0E3D22"/>
      <rect x="49" y="20" width="22" height="12" fill="#176B35"/>
      <rect x="28" y="30" width="64" height="166" rx="20" fill="#2E9E43"/>
      <path d="M28 128 h64 v48 a20 20 0 0 1-20 20 H48 a20 20 0 0 1-20-20 Z" fill="#E0A32E"/>
      <rect x="36" y="42" width="7" height="146" rx="3.5" fill="#FFFFFF" opacity="0.18"/>
      <rect x="34" y="68" width="52" height="82" rx="6" fill="#FFFFFF"/>
      <rect x="34" y="68" width="52" height="20" rx="6" fill="#0E3D22"/>
      <rect x="34" y="80" width="52" height="8" fill="#0E3D22"/>
      <use href="#curl" x="46" y="94" width="28" height="28" style="color:#2e9e43"/>
      <rect x="47" y="126" width="26" height="4" rx="2" fill="#15251A" opacity="0.45"/>
      <rect x="51" y="135" width="18" height="3" rx="1.5" fill="#15251A" opacity="0.3"/>
    </symbol>
    <symbol id="asm-tablets" viewBox="0 0 120 200">
      <rect x="34" y="6" width="52" height="22" rx="6" fill="#0E3D22"/>
      <rect x="28" y="28" width="64" height="168" rx="18" fill="#2E9E43"/>
      <rect x="36" y="40" width="7" height="146" rx="3.5" fill="#FFFFFF" opacity="0.18"/>
      <rect x="34" y="66" width="52" height="86" rx="6" fill="#FFFFFF"/>
      <rect x="34" y="66" width="52" height="20" rx="6" fill="#0E3D22"/>
      <rect x="34" y="78" width="52" height="8" fill="#0E3D22"/>
      <use href="#curl" x="46" y="92" width="28" height="28" style="color:#2e9e43"/>
      <rect x="47" y="124" width="26" height="4" rx="2" fill="#15251A" opacity="0.45"/>
      <circle cx="46" cy="176" r="12" fill="#F7F8F4" stroke="#176B35" stroke-width="2.5"/>
      <circle cx="70" cy="182" r="12" fill="#F7F8F4" stroke="#176B35" stroke-width="2.5"/>
      <line x1="38" y1="176" x2="54" y2="176" stroke="#176B35" stroke-width="2.5" stroke-linecap="round"/>
      <line x1="62" y1="182" x2="78" y2="182" stroke="#176B35" stroke-width="2.5" stroke-linecap="round"/>
    </symbol>
  </defs>
</svg>`;

export type FormKey = 'liquid' | 'tablets';

// ensureMotifDefs injects the SVG defs once per document so the leaf clip-path
// and the drawn products resolve. Idempotent.
export function ensureMotifDefs(): void {
  if (document.getElementById(DEFS_ID)) return;
  const node = fromHTML(DEFS);
  node.id = DEFS_ID;
  document.body.appendChild(node);
}

// formOf derives which bottle to draw from a product's category / name / sku.
// Defaults to liquid when nothing signals tablets.
export function formOf(p: { category?: string; name?: string; sku?: string }): FormKey {
  const hay = `${p.category ?? ''} ${p.name ?? ''} ${p.sku ?? ''}`.toLowerCase();
  return /tablet|caplet|\bcap\b|pill/.test(hay) ? 'tablets' : 'liquid';
}

export function formLabel(form: FormKey): string {
  return form === 'tablets' ? 'Tablets' : 'Liquid';
}

// fromHTML builds one element from a static SVG/HTML string (never user data).
function fromHTML(html: string): HTMLElement {
  const tpl = document.createElement('template');
  tpl.innerHTML = html.trim();
  return tpl.content.firstElementChild as HTMLElement;
}

// frondField returns the leaf-masked field holding the drawn product.
export function frondField(form: FormKey): HTMLElement {
  return fromHTML(
    `<div class="frond-field"><svg class="frond-field__product" viewBox="0 0 120 200" aria-hidden="true"><use href="#asm-${form}"></use></svg></div>`,
  );
}

// curl returns an inline fiddlehead SVG for bullets, bands, and the buy button.
export function curl(className: string): HTMLElement {
  return fromHTML(`<svg class="${className}" aria-hidden="true"><use href="#curl"></use></svg>`);
}

// --- Product photography -------------------------------------------------
//
// The storefront renders a product's real photos when it carries them and
// falls back to the drawn frond art otherwise, so a product with no photos
// (e.g. C+ Liquid until liquid photography exists) stays drawn. Image URLs are
// admin-set and server-validated; we additionally gate the scheme here so only
// same-origin paths and http(s) URLs ever reach an <img src> — defence in depth
// against a javascript:/data: value ever slipping through.

function isRenderableImageUrl(src: string): boolean {
  // Root-relative (same origin), e.g. the committed /images/products/*.jpg.
  if (src.startsWith('/') && !src.startsWith('//')) return true;
  try {
    const u = new URL(src, location.origin);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

// imagesOf returns a product's renderable photo URLs: the gallery when present,
// otherwise the single imageUrl, otherwise [] (the caller draws the frond art).
export function imagesOf(p: { imageUrl?: string; images?: string[] }): string[] {
  const gallery = (p.images ?? [])
    .map((s) => (typeof s === 'string' ? s.trim() : ''))
    .filter((s) => s !== '' && isRenderableImageUrl(s));
  if (gallery.length > 0) return gallery;
  const single = p.imageUrl?.trim();
  return single && isRenderableImageUrl(single) ? [single] : [];
}

// productPhoto builds a lazy, non-shifting <img> for a product photo. alt comes
// from the product name; decorative thumbnails pass an empty alt and are named
// by their button instead. The 4/5 box is reserved in CSS so the photo cannot
// shift the layout as it loads.
export function productPhoto(src: string, alt: string, className: string): HTMLImageElement {
  const img = document.createElement('img');
  img.className = className;
  img.src = src;
  img.alt = alt;
  img.loading = 'lazy';
  img.decoding = 'async';
  return img;
}
